import React, { useState, useEffect } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
 } from '@mui/material';
import { fetchCountries, fetchRegions } from "../../../redux/actions/countries.actions";
import { useSearchParams } from "react-router-dom";
import { createDealership } from "../../../redux/actions/dealership.actions";
import { clearError } from "../../../redux/reducers/dealership.reducer";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";

const AddDealershipModal: React.FC<ModalProps> = ({open, setOpen}) => {
  const dispatch: AppDispatch = useDispatch();
  const searchParams = useSearchParams()[0];
  const {error, loading} = useSelector((state: RootState) => state.dealership);
  const {data:countries} = useSelector((state: RootState) => state.countries);
  const {data:regions} = useSelector((state: RootState) => state.regions);

  // Text Input
  const [regionInput, setRegionInput] = useState("");
  const [countryInput, setCountryInput] = useState<any>("");

  const [confirmed, setConfirmed] = useState(false);

  const [updateInfo, setUpdateInfo] = useState<any>(null);

  const handleClose = () => {
    handleReset();
    dispatch(clearError());
    setOpen(false)
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchCountries());
      if (countryInput!=='') {
        dispatch(fetchRegions(countryInput));
      }
    }
  }, [dispatch,open,countryInput]);

  const handleSelect = (event: SelectChangeEvent, selectField: string) => {
    switch (selectField){
      case 'country':
        setCountryInput(event.target.value as string);
        setUpdateInfo({...updateInfo, country_id:event.target.value as string})
        setRegionInput('');
        break;

      case 'region':
        setRegionInput(event.target.value as string);
        setUpdateInfo({...updateInfo, region_id:event.target.value as string})
        break;
    }
  };

  const handleEdit = async (e:any) => {
    e.preventDefault();

    setConfirmed(true);

    if (updateInfo) await dispatch(createDealership({...updateInfo,params:searchParams.toString()}));

  }

  const handleReset = () => {
    setCountryInput("");
    setRegionInput("");
    setConfirmed(false);
  }

  const getFieldName = () => {
    switch (countryInput) {
      case 1:
        return 'State'

      case 2:
          return 'Province'
      default:
          return 'Region'
    }
  }

  const failed = Boolean(error && confirmed);
  const done = failed || confirmed;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={loading || done ? "sm" : "md"}
      busy={loading}
      tone={loading ? undefined : failed ? "error" : confirmed ? "success" : undefined}
      title={loading ? "Adding dealership…" : failed ? "An error occurred" : confirmed ? "Dealership added" : "Add new dealership"}
      actions={loading ? undefined : done ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEdit} disabled={loading}>Add</button>
        </>
      }
    >
      {loading ?
        <ModalLoading/>
        : failed ?
        <p>{error}</p>
        : confirmed ?
        <p>Dealership successfully added.</p>
        :
        <form className="dlg-section" style={{gap: '1rem'}} onSubmit={handleEdit}>
          <div className="dlg-row">
            <TextField
              id="name"
              label="Name"
              variant="outlined"
              fullWidth
              required
              onChange={(e)=>setUpdateInfo({...updateInfo, name:e.target.value})}
              autoComplete="off"
              />
            <TextField
              id="city"
              label="City"
              variant="outlined"
              fullWidth
              required
              onChange={(e)=>setUpdateInfo({...updateInfo, city:e.target.value})}
              autoComplete="off"
              />
          </div>

          <div className="dlg-row">
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={countryInput}
                onChange={e => handleSelect(e,'country')}
              >
                <MenuItem value={''}>All</MenuItem>
                { countries?.length>0 &&
                  countries.map((country:any)=>
                    <MenuItem key={country?.id} value={country?.id}>{country?.label}</MenuItem>
                  )
                }
              </Select>
            </FormControl>

            <FormControl fullWidth required={regions.length>0}>
              <InputLabel>{countryInput==='' || regions.length>0 ? getFieldName() : `No Regions Found.`}</InputLabel>
              <Select
                label={countryInput==='' || regions.length>0 ? getFieldName() : `No Regions Found.`}
                value={regionInput}
                onChange={e => handleSelect(e,'region')}
                disabled={regions.length===0}
              >
                <MenuItem value={''}>All</MenuItem>
                { regions?.length>0 && countryInput!=='' &&
                  regions.map((region:any)=>
                    <MenuItem key={region?.id} value={region?.id}>{region?.label}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
          </div>

          <div className="dlg-row">
            <TextField
              id="phone"
              label="Phone"
              variant="outlined"
              fullWidth
              required
              onChange={(e)=>setUpdateInfo({...updateInfo, phone:e.target.value})}
              autoComplete="off"
              />
            <TextField
              id="email"
              label="Email"
              variant="outlined"
              fullWidth
              required
              onChange={(e)=>setUpdateInfo({...updateInfo, email:e.target.value})}
              autoComplete="off"
              />
          </div>

          <div className="dlg-row">
            <TextField
              id="website_url"
              label="Website"
              variant="outlined"
              fullWidth
              onChange={(e)=>setUpdateInfo({...updateInfo, website_url:e.target.value})}
              autoComplete="off"
              />
            <TextField
              id="supplies_url"
              label="Supplies"
              variant="outlined"
              fullWidth
              onChange={(e)=>setUpdateInfo({...updateInfo, supplies_url:e.target.value})}
              autoComplete="off"
              />
          </div>
        </form>
      }
    </ModalShell>
  );
};

export default AddDealershipModal;
